// RRULE összeállítása és magyar összefoglalója (W21).
//
// Az ismétlődő projekteket a Flutter oldal is RRULE-lal tárolja (`rrule` mező
// a taskChange dokumentumon), ezért itt ugyanazt a szabványos formátumot kell
// előállítani — nem saját leírást.
//
// Tisztán, hálózat és DOM nélkül, hogy tesztelhető legyen.

import { t } from './i18n';

export type Frequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export type Weekday = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';

export type MonthlyMode =
  /// A hónap adott napján (pl. minden hónap 1-jén).
  | { kind: 'dayOfMonth'; day: number }
  /// A hónap n-edik adott hétköznapján (pl. első hétfő; -1 = utolsó).
  | { kind: 'weekdayOfMonth'; ordinal: number; weekday: Weekday };

export type Recurrence = {
  frequency: Frequency;
  /// Milyen sűrűn: minden 1., 2., 3. … időszakban.
  interval: number;
  /// Heti ismétlésnél a napok.
  weekdays?: Weekday[];
  monthly?: MonthlyMode;
  end: { kind: 'never' } | { kind: 'until'; date: Date } | { kind: 'count'; count: number };
};

export const weekdayOrder: Weekday[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

/// A napok neve ALANYESETBEN — a heti ismétlődés felsorolásához.
const weekdayKeys: Record<Weekday, string> = {
  MO: 'recur.dayMon',
  TU: 'recur.dayTue',
  WE: 'recur.dayWed',
  TH: 'recur.dayThu',
  FR: 'recur.dayFri',
  SA: 'recur.daySat',
  SU: 'recur.daySun',
};

export function weekdayName(day: Weekday): string {
  return t(weekdayKeys[day]);
}

/// A napok neve „a hónap első ___" alakban.
///
/// KÜLÖN kulcsok, nem a név + rag: magyarul a rag a szótól függ. A korábbi
/// `weekdayNames[day] + 'jén'` összefűzés a hét hét napjából ÖTRE hibás
/// alakot adott — „szerdajén", „csütörtökjén", „péntekjén", „szombatjén",
/// „vasárnapjén". A helyes alak most a szótárban áll.
const weekdayOnKeys: Record<Weekday, string> = {
  MO: 'recur.onMon',
  TU: 'recur.onTue',
  WE: 'recur.onWed',
  TH: 'recur.onThu',
  FR: 'recur.onFri',
  SA: 'recur.onSat',
  SU: 'recur.onSun',
};

const monthKeys = [
  'recur.monthJan', 'recur.monthFeb', 'recur.monthMar', 'recur.monthApr',
  'recur.monthMay', 'recur.monthJun', 'recur.monthJul', 'recur.monthAug',
  'recur.monthSep', 'recur.monthOct', 'recur.monthNov', 'recur.monthDec',
];

const ordinalKeys: Record<number, string> = {
  1: 'recur.ordinal1',
  2: 'recur.ordinal2',
  3: 'recur.ordinal3',
  4: 'recur.ordinal4',
  [-1]: 'recur.ordinalLast',
};

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/// A szabvány UNTIL mezője UTC-ben, `Z` végződéssel.
function untilValue(date: Date): string {
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
  );
}

export function toRRule(recurrence: Recurrence): string {
  const parts: string[] = [`FREQ=${recurrence.frequency.toUpperCase()}`];

  // Az INTERVAL=1 az alapértelmezés, de kiírjuk, mert így a tárolt érték
  // önmagában is egyértelmű, és a Flutter oldali olvasó sem tippel.
  parts.push(`INTERVAL=${Math.max(1, Math.trunc(recurrence.interval))}`);

  if (recurrence.frequency === 'weekly' && recurrence.weekdays?.length) {
    const ordered = weekdayOrder.filter((day) => recurrence.weekdays!.includes(day));
    parts.push(`BYDAY=${ordered.join(',')}`);
  }

  if (recurrence.frequency === 'monthly' && recurrence.monthly) {
    if (recurrence.monthly.kind === 'dayOfMonth') {
      parts.push(`BYMONTHDAY=${recurrence.monthly.day}`);
    } else {
      parts.push(`BYDAY=${recurrence.monthly.ordinal}${recurrence.monthly.weekday}`);
    }
  }

  if (recurrence.end.kind === 'until') {
    parts.push(`UNTIL=${untilValue(recurrence.end.date)}`);
  } else if (recurrence.end.kind === 'count') {
    parts.push(`COUNT=${Math.max(1, Math.trunc(recurrence.end.count))}`);
  }

  return parts.join(';');
}

function intervalPhrase(recurrence: Recurrence): string {
  const n = Math.max(1, Math.trunc(recurrence.interval));
  switch (recurrence.frequency) {
    case 'daily':
      return n === 1 ? t('recur.everyDay') : t('recur.everyNDays', [String(n)]);
    case 'weekly':
      return n === 1 ? t('recur.everyWeek') : t('recur.everyNWeeks', [String(n)]);
    case 'monthly':
      return n === 1 ? t('recur.everyMonth') : t('recur.everyNMonths', [String(n)]);
    case 'yearly':
      return n === 1 ? t('recur.everyYear') : t('recur.everyNYears', [String(n)]);
  }
}

/// Emberi nyelvű összefoglaló — a tervek szerint ez látszik a szerkesztő alatt.
export function describe(recurrence: Recurrence): string {
  const pieces: string[] = [intervalPhrase(recurrence)];

  if (recurrence.frequency === 'weekly' && recurrence.weekdays?.length) {
    const ordered = weekdayOrder
      .filter((day) => recurrence.weekdays!.includes(day))
      .map((day) => weekdayName(day));
    pieces.push(ordered.join(', '));
  }

  if (recurrence.frequency === 'monthly' && recurrence.monthly) {
    if (recurrence.monthly.kind === 'dayOfMonth') {
      pieces.push(t('recur.onDayOfMonth', [String(recurrence.monthly.day)]));
    } else {
      const ordinalKey = ordinalKeys[recurrence.monthly.ordinal];
      const ordinal = ordinalKey ? t(ordinalKey) : `${recurrence.monthly.ordinal}.`;
      pieces.push(
        t('recur.onWeekdayOfMonth', [
          ordinal,
          t(weekdayOnKeys[recurrence.monthly.weekday]),
        ]),
      );
    }
  }

  let summary = pieces.join(', ');

  if (recurrence.end.kind === 'until') {
    const date = recurrence.end.date;
    summary += t('recur.untilDate', [
      t('recur.longDate', [
        String(date.getFullYear()),
        t(monthKeys[date.getMonth()] ?? ''),
        String(date.getDate()),
      ]),
    ]);
  } else if (recurrence.end.kind === 'count') {
    summary += t('recur.countTotal', [String(recurrence.end.count)]);
  }

  return `${summary}.`;
}

/// Ellenőrzés mentés előtt. Üres tömb = érvényes.
export function validate(recurrence: Recurrence): string[] {
  const problems: string[] = [];

  if (!Number.isFinite(recurrence.interval) || recurrence.interval < 1) {
    problems.push('Az ismétlés gyakorisága legalább 1 legyen.');
  }

  if (recurrence.frequency === 'weekly' && (recurrence.weekdays?.length ?? 0) === 0) {
    // Nap nélküli heti ismétlés sosem futna le.
    problems.push('Heti ismétlésnél válassz legalább egy napot.');
  }

  if (recurrence.frequency === 'monthly' && recurrence.monthly?.kind === 'dayOfMonth') {
    const day = recurrence.monthly.day;
    if (day < 1 || day > 31) {
      problems.push('A hónap napja 1 és 31 között lehet.');
    } else if (day > 28) {
      // Nem hiba, de figyelmeztetes: februarban kimaradhat.
      problems.push('A 28-nál nagyobb napszám rövidebb hónapokban kimaradhat.');
    }
  }

  if (recurrence.end.kind === 'count' && recurrence.end.count < 1) {
    problems.push('Az alkalmak száma legalább 1 legyen.');
  }

  if (recurrence.end.kind === 'until' && recurrence.end.date.getTime() < Date.now()) {
    problems.push('A vége dátum a múltban van, így egyetlen alkalom sem jönne létre.');
  }

  return problems;
}

/// Tárolt RRULE visszaolvasása.
///
/// Eddig csak ÍRNI tudtunk RRULE-t (`toRRule`), olvasni nem: az ismétlődő
/// projekteknél ezért sehol nem látszott, MILYEN ütem szerint ismétlődnek —
/// csak annyi, hogy ismétlődnek. Ismeretlen vagy hibás szabálynál `undefined`
/// jön vissza; kitalált ütemet nem mutatunk.
export function parseRRule(text: string): Recurrence | undefined {
  const parts = new Map<string, string>();
  for (const piece of text.replace(/^RRULE:/i, '').split(';')) {
    const [key, value] = piece.split('=');
    if (key && value) parts.set(key.trim().toUpperCase(), value.trim());
  }

  const freqMap: Record<string, Frequency> = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
  };
  const frequency = freqMap[(parts.get('FREQ') ?? '').toUpperCase()];
  if (!frequency) return undefined;

  const interval = Number(parts.get('INTERVAL') ?? '1');

  const byDay = (parts.get('BYDAY') ?? '')
    .split(',')
    .map((day) => day.trim().toUpperCase())
    .filter((day) => day.length > 0);

  const recurrence: Recurrence = {
    frequency,
    interval: Number.isFinite(interval) && interval >= 1 ? interval : 1,
    end: { kind: 'never' },
  };

  if (frequency === 'weekly' && byDay.length > 0) {
    recurrence.weekdays = byDay.filter((day): day is Weekday =>
      weekdayOrder.includes(day as Weekday),
    );
  }

  if (frequency === 'monthly') {
    const monthDay = Number(parts.get('BYMONTHDAY') ?? '');
    if (Number.isFinite(monthDay) && monthDay !== 0) {
      recurrence.monthly = { kind: 'dayOfMonth', day: monthDay };
    } else if (byDay.length === 1) {
      // Pl. „2FR" = a honap masodik pentekje, „-1MO" = az utolso hetfo.
      const match = /^(-?\d+)([A-Z]{2})$/.exec(byDay[0]!);
      if (match && weekdayOrder.includes(match[2] as Weekday)) {
        recurrence.monthly = {
          kind: 'weekdayOfMonth',
          ordinal: Number(match[1]),
          weekday: match[2] as Weekday,
        };
      }
    }
  }

  const count = Number(parts.get('COUNT') ?? '');
  const until = parts.get('UNTIL');
  if (Number.isFinite(count) && count > 0) {
    recurrence.end = { kind: 'count', count };
  } else if (until) {
    // `20261231T235959Z`
    const match = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})Z?)?$/.exec(until);
    if (match) {
      recurrence.end = {
        kind: 'until',
        date: new Date(
          Date.UTC(
            Number(match[1]),
            Number(match[2]) - 1,
            Number(match[3]),
            Number(match[4] ?? '0'),
            Number(match[5] ?? '0'),
            Number(match[6] ?? '0'),
          ),
        ),
      };
    }
  }

  return recurrence;
}

/// Az ismétlődés rövid, emberi leírása a tárolt szabályból — vagy `undefined`,
/// ha a szabály nem értelmezhető.
export function describeRRule(text: string | undefined): string | undefined {
  if (!text) return undefined;
  const recurrence = parseRRule(text);
  return recurrence ? describe(recurrence) : undefined;
}

/// A következő alkalmak kiszámolása a szabályból.
///
/// A terv (W21) az összefoglaló alatt chipként mutatja a következő
/// időpontokat: ez az egyetlen visszajelzés arról, hogy a beállított szabály
/// tényleg azt jelenti, amit a felhasználó gondol. Számolt érték, nem
/// kitalált adat — ugyanaz a szabály adja, ami mentésre kerül.
export function nextOccurrences(
  recurrence: Recurrence,
  from: Date = new Date(),
  limit = 12,
): Date[] {
  const results: Date[] = [];
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const interval = Math.max(1, Math.floor(recurrence.interval));
  const maxCount =
    recurrence.end.kind === 'count' ? Math.min(limit, recurrence.end.count) : limit;
  const until = recurrence.end.kind === 'until' ? recurrence.end.date : undefined;

  function push(date: Date): boolean {
    if (date.getTime() < start.getTime()) return true;
    if (until && date.getTime() > until.getTime()) return false;
    results.push(date);
    return results.length < maxCount;
  }

  if (recurrence.frequency === 'daily') {
    const cursor = new Date(start);
    while (results.length < maxCount) {
      if (!push(new Date(cursor))) break;
      cursor.setDate(cursor.getDate() + interval);
    }
    return results;
  }

  if (recurrence.frequency === 'weekly') {
    const days = (recurrence.weekdays ?? []).map((day) => weekdayOrder.indexOf(day));
    if (days.length === 0) return results;

    // A hetet hetfovel kezdjuk, ahogy a magyar naptar.
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));

    for (let week = 0; results.length < maxCount && week < limit * interval * 2; week += interval) {
      let stop = false;
      for (const dayIndex of [...days].sort((a, b) => a - b)) {
        const date = new Date(weekStart);
        date.setDate(date.getDate() + week * 7 + dayIndex);
        if (!push(date)) {
          stop = true;
          break;
        }
      }
      if (stop) break;
    }
    return results;
  }

  if (recurrence.frequency === 'monthly') {
    const mode = recurrence.monthly ?? { kind: 'dayOfMonth' as const, day: 1 };
    for (let step = 0; results.length < maxCount && step < limit * interval * 3; step += interval) {
      const month = new Date(start.getFullYear(), start.getMonth() + step, 1);
      let date: Date | undefined;

      if (mode.kind === 'dayOfMonth') {
        const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
        // A rovidebb honapokbol a 29–31. nap kimarad — ezt a `validate` is
        // figyelmeztetesben mondja; itt egyszeruen atugorjuk.
        if (mode.day <= lastDay) {
          date = new Date(month.getFullYear(), month.getMonth(), mode.day);
        }
      } else {
        const target = weekdayOrder.indexOf(mode.weekday);
        if (mode.ordinal > 0) {
          const first = new Date(month);
          const shift = (target - ((first.getDay() + 6) % 7) + 7) % 7;
          const day = 1 + shift + (mode.ordinal - 1) * 7;
          const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
          if (day <= lastDay) date = new Date(month.getFullYear(), month.getMonth(), day);
        } else {
          const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
          const shift = (((last.getDay() + 6) % 7) - target + 7) % 7;
          date = new Date(month.getFullYear(), month.getMonth(), last.getDate() - shift);
        }
      }

      if (date && !push(date)) break;
    }
    return results;
  }

  // Eves: ugyanaz a nap es honap, evente.
  const cursor = new Date(start);
  while (results.length < maxCount) {
    if (!push(new Date(cursor))) break;
    cursor.setFullYear(cursor.getFullYear() + interval);
  }
  return results;
}
